import { Badge } from '../ui/badge';
import { ENTITY_CHOICES } from '../../lib/importAPI';

const Section = ({ title, rows, children, testid }) => (
  <details className="rounded border p-3" open={rows.length > 0 && rows.length < 50} data-testid={testid}>
    <summary className="cursor-pointer text-sm font-medium">{title} ({rows.length})</summary>
    <div className="mt-2 space-y-2 max-h-96 overflow-y-auto">{children}</div>
  </details>
);

const Line = ({ r, children }) => (
  <div className="text-xs border-b pb-2" data-testid={`import-row-${r.row}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-muted-foreground">Row {r.row}</span><span className="font-medium">{r.name || '(no name)'}</span>
      {r.entity_type && <Badge variant="outline">{r.entity_type}</Badge>}
      {r.abn && <span>ABN {r.abn}</span>}{r.tfn_masked && <span>TFN {r.tfn_masked}</span>}{r.email && <span>{r.email}</span>}
    </div>
    {r.errors?.map((e) => <p key={e} className="text-red-600">{e}</p>)}
    {r.warnings?.map((w) => <p key={w} className="text-amber-700">{w}</p>)}
    {r.reason && <p className="text-muted-foreground">{r.reason}</p>}
    {children}
  </div>
);

export const PreviewStep = ({ pv, updates, setUpdates, amb, setAmb, entityValues, setEntityValues }) => {
  const tick = (row, f, on) => setUpdates({ ...updates, [row]: on ? [...(updates[row] || []), f] : (updates[row] || []).filter((x) => x !== f) });
  const unmapped = [...new Set(pv.invalid.flatMap((r) => r.errors).map((e) => (e.match(/^Entity type '(.+)' needs mapping/) || [])[1]).filter((x) => x && x !== '(blank)'))];
  return (
    <div className="space-y-3" data-testid="import-preview">
      <div className="flex flex-wrap gap-2 text-sm">
        {Object.entries(pv.summary).map(([k, v]) => <Badge key={k} variant="secondary" data-testid={`import-count-${k}`}>{k}: {v}</Badge>)}
      </div>
      {unmapped.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs space-y-1" data-testid="import-entity-mapping">
          <p className="font-medium">Map business structures</p>
          {unmapped.map((v) => (
            <label key={v} className="flex items-center gap-2"><span className="w-48">{v}</span>
              <select className="h-7 rounded border bg-background px-2" value={entityValues[v] || ''} onChange={(e) => setEntityValues({ ...entityValues, [v]: e.target.value })} data-testid={`entity-map-${v}`}>
                <option value="">—</option>{ENTITY_CHOICES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></label>
          ))}
        </div>
      )}
      <Section title="New clients (created active, no portal invite)" rows={pv.new} testid="import-new">{pv.new.map((r) => <Line key={r.row} r={r} />)}</Section>
      <Section title="Matched existing clients – tick the fields to update" rows={pv.matched} testid="import-matched">
        {pv.matched.map((r) => (
          <Line key={r.row} r={r}>
            <p className="mt-1">Matches <b>{r.client_name}</b> by {r.matched_on.toUpperCase()}{!r.fields.length && ' – no differences'}</p>
            {r.fields.length > 0 && (
              <table className="mt-1 w-full"><tbody>
                {r.fields.map((f) => (
                  <tr key={f.field} className="border-t">
                    <td className="w-6"><input type="checkbox" checked={(updates[r.row] || []).includes(f.field)} onChange={(e) => tick(r.row, f.field, e.target.checked)} data-testid={`upd-${r.row}-${f.field}`} /></td>
                    <td className="w-32">{f.field}</td><td className="text-muted-foreground">{f.old || '—'}</td><td>→ {f.new}</td>
                  </tr>
                ))}
              </tbody></table>
            )}
          </Line>
        ))}
      </Section>
      <Section title="Ambiguous – choose a client or skip" rows={pv.ambiguous} testid="import-ambiguous">
        {pv.ambiguous.map((r) => (
          <Line key={r.row} r={r}>
            <select className="mt-1 h-7 rounded border bg-background px-2" value={amb[r.row] || 'skip'} onChange={(e) => setAmb({ ...amb, [r.row]: e.target.value })} data-testid={`amb-${r.row}`}>
              <option value="skip">Skip this row</option>{r.candidates.map((c) => <option key={c.id} value={c.id}>Update {c.name}{c.abn ? ` (ABN ${c.abn})` : ''}</option>)}
            </select>
          </Line>
        ))}
      </Section>
      <Section title="Invalid" rows={pv.invalid} testid="import-invalid">{pv.invalid.map((r) => <Line key={r.row} r={r} />)}</Section>
      <Section title="Skipped" rows={pv.skipped} testid="import-skipped">{pv.skipped.map((r) => <Line key={r.row} r={r} />)}</Section>
    </div>
  );
};
